"use client";

import { useEffect, useState } from "react";

/**
 * Published support-desk hours: Mon–Sat, 12:30–21:30 IST. Kept here so the
 * contact card header and the surrounding trust panel agree on one source of
 * truth for "are we online right now?".
 */
const OPEN_MINUTES = 12 * 60 + 30; // 12:30
const CLOSE_MINUTES = 21 * 60 + 30; // 21:30

/** True when `now` falls inside published IST support hours (Mon–Sat). */
export function isSupportOnline(now: Date): boolean {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);

  const weekday = parts.find((part) => part.type === "weekday")?.value;
  const hour = Number(parts.find((part) => part.type === "hour")?.value);
  const minute = Number(parts.find((part) => part.type === "minute")?.value);

  if (weekday === "Sun" || Number.isNaN(hour) || Number.isNaN(minute)) return false;

  const minutes = hour * 60 + minute;
  return minutes >= OPEN_MINUTES && minutes < CLOSE_MINUTES;
}

/**
 * Live support status. Starts `null` (unknown) so the server render and the
 * first client render match — then resolves on mount and re-checks each minute,
 * so the dot flips on its own when the desk opens or closes.
 */
export function useSupportStatus(): { online: boolean | null } {
  const [online, setOnline] = useState<boolean | null>(null);

  useEffect(() => {
    const update = () => setOnline(isSupportOnline(new Date()));
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, []);

  return { online };
}
